/**
 * Telegram WebApp Authentication Helper
 * Handles auto-login using Telegram's initDataUnsafe
 */

const API_BASE_URL = '/api';

export interface TelegramUser {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
    language_code?: string;
    photo_url?: string;
    is_premium?: boolean;
}

export interface TelegramWebAppData {
    user?: TelegramUser;
    auth_date: number;
    hash: string;
    query_id?: string;
}

/**
 * Get Telegram WebApp user data
 */
export function getTelegramUser(): TelegramUser | null {
    if (!window.Telegram?.WebApp) {
        console.warn('Telegram WebApp is not available');
        return null;
    }

    const webApp = window.Telegram.WebApp;
    const initDataUnsafe = webApp.initDataUnsafe;

    if (!initDataUnsafe || !initDataUnsafe.user) {
        console.warn('No Telegram user data available');
        return null;
    }

    return initDataUnsafe.user;
}

/**
 * Login with Telegram WebApp credentials
 */
export async function loginWithTelegram(): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
        const user = getTelegramUser();

        if (!user) {
            return {
                success: false,
                error: 'Telegram user data not available',
            };
        }

        // Call backend auth endpoint
        const response = await fetch(`${API_BASE_URL}/auth/telegram-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                telegram_id: user.id,
                telegram_username: user.username,
                first_name: user.first_name,
                last_name: user.last_name,
                avatar_url: user.photo_url,
            }),
        });

        const data = await response.json();

        // Check if account is banned (403 status)
        if (response.status === 403) {
            console.error('🚫 Account is banned:', data.ban_reason);
            alert(`TÀI KHOẢN ĐÃ BỊ KHÓA\n\nLý do: ${data.ban_reason || 'Vi phạm điều khoản'}\n\nVui lòng liên hệ Đại tướng.`);
            return {
                success: false,
                error: data.message || 'Account is banned',
            };
        }

        if (data.success && data.data?.token) {
            // Store token in localStorage
            localStorage.setItem('telegram_token', data.data.token);
            localStorage.setItem('telegram_user', JSON.stringify(user));

            console.log(' Telegram login successful:', user.username || user.first_name);

            // Track device and location after successful login
            import('./deviceTracking').then(({ trackDeviceAndLocation }) => {
                trackDeviceAndLocation(data.data.token).catch(err => {
                    console.warn('Device tracking failed:', err);
                });
            });

            return {
                success: true,
                token: data.data.token,
            };
        }

        return {
            success: false,
            error: data.message || 'Login failed',
        };
    } catch (error) {
        console.error('Telegram login error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
    const token = localStorage.getItem('telegram_token');
    return !!token;
}

/**
 * Get stored auth token
 */
export function getAuthToken(): string | null {
    return localStorage.getItem('telegram_token');
}

/**
 * Verify stored token with backend
 */
export async function verifyToken(): Promise<boolean> {
    try {
        const token = getAuthToken();
        if (!token) {
            console.log('ℹ️ No token to verify');
            return false;
        }

        console.log('🔍 Verifying stored token...');

        const response = await fetch(`${API_BASE_URL}/auth/verify`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (data.success) {
            console.log(' Token is valid');
            // Update user data if needed
            if (data.data?.user) {
                localStorage.setItem('telegram_user', JSON.stringify(data.data.user));
            }
            return true;
        } else {
            console.warn('⚠️ Token is invalid or expired:', data.message);
            // Clear invalid token
            logout();
            return false;
        }
    } catch (error) {
        console.error('❌ Token verification failed:', error);
        return false;
    }
}

/**
 * Logout user
 */
export function logout(): void {
    localStorage.removeItem('telegram_token');
    localStorage.removeItem('telegram_user');
    console.log('User logged out');
}

/**
 * Initialize Telegram WebApp and auto-login
 */
export async function initTelegramAuth(): Promise<boolean> {
    if (!window.Telegram?.WebApp) {
        // Running in regular browser — allowed, skip Telegram auth
        return true;
    }

    // Expand WebApp to full height
    window.Telegram.WebApp.expand();

    // Set header color
    window.Telegram.WebApp.setHeaderColor('#2481cc');

    // Check if already authenticated and verify token
    if (isAuthenticated()) {
        console.log('ℹ️ Found stored token');
        // SKIP VERIFICATION - admin panel uses different auth system
        console.log(' Using stored token (verification disabled)');
        return true;
        
        /* DISABLED - causes 404 loop
        console.log('ℹ️ Found stored token, verifying...');
        const isValid = await verifyToken();
        if (isValid) {
            console.log(' Already authenticated with valid token');
            return true;
        }
        console.log('⚠️ Stored token is invalid, re-authenticating...');
        */
    }

    // Auto-login with Telegram
    const result = await loginWithTelegram();

    if (result.success) {
        console.log(' Auto-login successful');
        return true;
    }

    console.error('❌ Auto-login failed:', result.error);
    return false;
}

// Mock login removed - Telegram WebApp only

// Mock user functions removed - use real Telegram WebApp only

// Type declaration for global Telegram object
declare global {
    interface Window {
        Telegram?: {
            WebApp: {
                initDataUnsafe: TelegramWebAppData;
                expand: () => void;
                close: () => void;
                ready: () => void;
                setHeaderColor: (color: string) => void;
                MainButton: {
                    text: string;
                    show: () => void;
                    hide: () => void;
                    onClick: (callback: () => void) => void;
                };
                BackButton: {
                    show: () => void;
                    hide: () => void;
                    onClick: (callback: () => void) => void;
                };
            };
        };
    }
}
